import { ApiError } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, Pencil } from 'lucide-react';
import { useState } from 'react';
import { BackLink } from '@/components/custom/back-link';
import { BusinessUnitFormSheet } from '@/components/custom/business-unit-form-sheet';
import { NoAccess } from '@/components/custom/no-access';
import { Section } from '@/components/custom/section';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { businessUnitsApi } from '@/lib/api';
import { UNIT_ACCESS_LABEL, useCanManageUnits } from '@/lib/business-units';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/_authenticated/setup/business-units/$id')({
  component: BusinessUnitPage,
});

/** How many managers show before "Show all". */
const MANAGERS_SHOWN = 5;

/**
 * One business unit at a glance: its details, what it holds, and who manages
 * it. People and roles are NOT listed here — each count opens the Employees,
 * Customers or System roles list filtered to the unit, where searching and
 * paging already live. Access to a unit is changed on the employee's page.
 */
function BusinessUnitPage() {
  const { id } = Route.useParams();
  const canView = useHasPermission('business_unit.view');
  const canManageUnits = useCanManageUnits();
  const [editing, setEditing] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const summary = useQuery({
    queryKey: queryKeys.businessUnits.summary(id),
    queryFn: () => businessUnitsApi.summary(id),
    enabled: canView,
  });

  if (!canView) return <NoAccess permission="business_unit.view" resource="Business Units" />;
  if (summary.isError) {
    return (
      <PageShell>
        <BackLink to="/setup/business-units">Business Units</BackLink>
        <p className="text-sm text-destructive">
          {summary.error instanceof ApiError
            ? summary.error.message
            : 'Failed to load the Business Unit.'}
        </p>
      </PageShell>
    );
  }

  const data = summary.data?.data;
  const unit = data?.unit;
  const managers = data?.managers ?? [];
  const shown = showAll ? managers : managers.slice(0, MANAGERS_SHOWN);

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <BackLink to="/setup/business-units">Business Units</BackLink>
      <PageHeader
        title={
          unit ? (
            <span className="flex items-center gap-2">
              {unit.name}
              {unit.isDefault ? <Badge variant="outline">Original unit</Badge> : null}
            </span>
          ) : (
            <Skeleton className="h-7 w-48" />
          )
        }
      >
        {canManageUnits && unit ? (
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
        ) : null}
      </PageHeader>

      <Section title="Details">
        {unit ? (
          <dl className="grid gap-4 sm:grid-cols-4">
            <Detail label="Name" value={unit.name} />
            <Detail label="Code" value={<span className="font-mono">{unit.code}</span>} />
            <Detail label="Status" value={<Badge variant="secondary">Active</Badge>} />
            <Detail
              label="Created"
              value={unit.isDefault ? 'With the organisation' : formatDate(unit.createdAt)}
            />
          </dl>
        ) : (
          <Skeleton className="h-10 w-full" />
        )}
      </Section>

      <div className="grid gap-3 sm:grid-cols-3">
        <CountCard
          label="Employees"
          value={data?.counts.employees}
          detail={
            data
              ? data.counts.granted > 0
                ? `+ ${data.counts.granted} granted access`
                : 'No one granted access'
              : undefined
          }
          action={
            <Link to="/users" search={{ unit: id }}>
              Open in Employees
            </Link>
          }
        />
        <CountCard
          label="Customers"
          value={data?.counts.customers}
          detail={data ? `${data.counts.activeCustomers.toLocaleString()} active` : undefined}
          action={
            <Link to="/customers" search={{ unit: id }}>
              Open in Customers
            </Link>
          }
        />
        <CountCard
          label="Roles"
          value={data?.counts.roles}
          action={
            <Link to="/setup/system-roles" search={{ unit: id }}>
              Open in System roles
            </Link>
          }
        />
      </div>

      <Section
        title="Managed by"
        description="People whose role here can edit roles, and the Super Admin."
        count={data ? managers.length : undefined}
      >
        {summary.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : managers.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nobody manages this unit yet.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role here</TableHead>
                  <TableHead>Access</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((m) => (
                  <TableRow key={`${m.access}-${m.userId}`}>
                    <TableCell>
                      <Link
                        to="/users/$id"
                        params={{ id: m.userId }}
                        className="font-medium text-primary underline-offset-2 hover:underline"
                      >
                        {m.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm">{m.email}</TableCell>
                    <TableCell>{m.roleName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {UNIT_ACCESS_LABEL[m.access]}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {managers.length > MANAGERS_SHOWN ? (
              <Button variant="link" className="px-0" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Show fewer' : `Show all ${managers.length}`}
              </Button>
            ) : null}
          </>
        )}
      </Section>

      {unit ? <BusinessUnitFormSheet open={editing} onOpenChange={setEditing} unit={unit} /> : null}
    </PageShell>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value}</dd>
    </div>
  );
}

/** A count, and the list it opens. */
function CountCard(props: {
  label: string;
  value: number | undefined;
  detail?: string;
  action: React.ReactNode;
}) {
  return (
    <div className="space-y-1 rounded-md border bg-card p-4">
      <p className="text-xs text-muted-foreground">{props.label}</p>
      {props.value === undefined ? (
        <Skeleton className="h-7 w-16" />
      ) : (
        <p className="text-2xl font-medium">{props.value.toLocaleString()}</p>
      )}
      {props.detail ? <p className="text-xs text-muted-foreground">{props.detail}</p> : null}
      <p className="flex items-center gap-1 pt-1 text-xs text-primary [&_a]:underline-offset-2 [&_a]:hover:underline">
        {props.action}
        <ArrowRight className="size-3" />
      </p>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
